import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { PunchSummary, PunchSummaryCategory } from "../../../../utils/models";

export type CategoryDescription = { labelKey: string; employeeCount: number; hasEntries: boolean; totalsText?: string };

const fmtHours = (n: number): string => n.toFixed(1).replace(/\.0$/, "");

const employeeCountForCategory = (summary: PunchSummary, category: PunchSummaryCategory): number => {
	switch (category) {
		case "noPunch":
			return summary.noPunch.length;
		case "notScheduled":
			return summary.notScheduled.employees.length;
		case "excludedFromPpd":
			return summary.excludedFromPpd.employees.length;
		case "missingPunch":
			return summary.missingPunch.length;
		case "shiftDelta":
			return summary.shiftDelta.employees.length;
		case "pto":
			return summary.pto.length;
		case "ot":
			return summary.ot.employees.length;
		case "callIn":
			return summary.callIn.employees.length;
		case "agency":
			return summary.agency.employees.length;
	}
};

const labelKeyForCategory = (category: PunchSummaryCategory): string => `punchSummary.category.${category}`;

export const describeBuckets = (summary: PunchSummary, category: PunchSummaryCategory): CategoryDescription => {
	const employeeCount = employeeCountForCategory(summary, category);
	return {
		labelKey: labelKeyForCategory(category),
		employeeCount,
		hasEntries: employeeCount > 0,
	};
};

export const useDescribeBuckets = (): ((summary: PunchSummary, category: PunchSummaryCategory) => CategoryDescription) => {
	const { t } = useTranslation();
	return useCallback(
		(summary, category) => {
			const base = describeBuckets(summary, category);
			if (!base.hasEntries) return base;

			let totalsText: string | undefined;
			switch (category) {
				case "notScheduled":
					totalsText = t("punchSummary.totals.hours", { hours: fmtHours(summary.notScheduled.totalActualHours) });
					break;
				case "excludedFromPpd":
					totalsText = t("punchSummary.totals.hours", { hours: fmtHours(summary.excludedFromPpd.totalActualHours) });
					break;
				case "shiftDelta":
					totalsText = t("punchSummary.totals.diff", { hours: fmtHours(summary.shiftDelta.totalDeltaHours) });
					break;
				case "ot":
					totalsText = t("punchSummary.totals.ot", { hours: fmtHours(summary.ot.totalOtHours) });
					break;
				case "agency":
					totalsText = t("punchSummary.totals.agency", { hours: fmtHours(summary.agency.totalAgencyHours) });
					break;
				default:
					break;
			}
			return { ...base, totalsText };
		},
		[t],
	);
};
